import { Modal } from "react-bootstrap";
import useTranslator from "../../../hooks/useTranslator";
import TenantContext from "../../../context/tenant/TenantContext";
import { useContext, useMemo, useRef, useState } from "react";
import SkorDefaultLoader from "../../../assets/img/skorDefaultLoader.gif";
import AuthContext from "../../../context/auth/AuthContext";
import { haveValue, toast } from "../../../helper/common";

const PendingRequestsPopup = ({
  handleAcceptModal,
  show,
  pendingGuarantees,
  loadPendingGuarantees,
  loading,
  setLoading,
  showBackground = false,
  setPendingFlatmatesRequests,
  setPendingGuaranteesRequests,
  setAcceptedGuaranteeName,
}) => {
  const { _t } = useTranslator();
  const { user } = useContext(AuthContext);
  const { linkGurantor, rejectGuarantee, submitConsents } =
    useContext(TenantContext);
  const { loadUserTenant } = useContext(AuthContext);
  const [processingIds, setProcessingIds] = useState(new Set());

  // useState drives the UI re-render (hides consent blocks once submitted).
  const [consentSubmitted, setConsentSubmitted] = useState(false);
  // useRef is the synchronous guard — checked before the API call so that
  // even if two Accepts fire in the same render cycle, only one goes through.
  const consentSubmittedRef = useRef(false);

  // Per-request consent state: { [tenantId]: { gdprCheck: bool, ... } }
  // Keyed by tenantId so each request card has independent checkboxes.
  const [requestConsents, setRequestConsents] = useState({});

  const [tab, setTab] = useState("info");
  const [rejectingId, setRejectingId] = useState({
    id: null,
    gurantorName: null,
  });

  const consentSections = [
    {
      id: "gdprCheck",
      title: _t("rentalInsuranceEvaluation"),
      points: [
        _t("insuranceCompanyWillCheckCreditHistory"),
        _t("usedToCalculateRentDefaultRisk"),
        _t("youCanWithdrawConsentBeforeApproval"),
      ],
      checkboxLabel: _t(
        "iConsentToTheProcessingOfMyPersonalDataForRentalInsuranceSolvencyEvaluation"
      ),
      links: [
        {
          title: _t("viewFullGdprNotice"),
          url: "/pdf/consents/gropo_muto_consent_gdpr.pdf",
        },
        {
          title: _t("skorConsent"),
          url: "/pdf/consents/skor_consent.pdf",
        },
      ],
    },
  ];

  // True if ANY request is currently being accepted.
  const isAnyProcessing = processingIds.size > 0;

  // Get the consent state for a specific request, defaulting all sections to false.
  const getConsentsForRequest = (tenantId) =>
    requestConsents[tenantId] ??
    consentSections.reduce((acc, s) => ({ ...acc, [s.id]: false }), {});

  // Toggle a single checkbox for a specific request card only.
  const handleToggle = (tenantId, sectionId) => {
    setRequestConsents((prev) => {
      const current =
        prev[tenantId] ??
        consentSections.reduce((acc, s) => ({ ...acc, [s.id]: false }), {});
      return {
        ...prev,
        [tenantId]: { ...current, [sectionId]: !current[sectionId] },
      };
    });
  };

  // Whether the consent block should be shown for a given request.
  // Hidden globally once submitted this session, or if user already has consent.
  const needsConsent = (request) => {
    if (consentSubmitted) return false;
    if (haveValue(user?.consents?.gdpr_consent)) return false;
    return request?.tenant_id?.referredId?.insurance_service_status === true;
  };

  // All consent checkboxes ticked for a specific request.
  const allConsentsTicked = (tenantId) => {
    const consents = getConsentsForRequest(tenantId);
    return consentSections.every((section) => consents[section.id]);
  };

  const handleSubmitConsent = async (tenantId) => {
    // Synchronous ref check — prevents a second in-flight Accept from
    // calling submitConsents again before the first await resolves.
    if (consentSubmittedRef.current) return;
    consentSubmittedRef.current = true;

    const consents = getConsentsForRequest(tenantId);
    const consentPayload = {};
    if (consents.gdprCheck) {
      consentPayload.gdpr_consent = true;
      consentPayload.skor_consent = true;
    }
    await submitConsents(consentPayload);
  };

  const handleAccept = async (request) => {
    const guaranteeId = request?.tenant_id?._id;
    if (processingIds.has(guaranteeId)) return;

    // Guard: consent block visible but not fully ticked for this request.
    if (needsConsent(request) && !allConsentsTicked(guaranteeId)) return;

    setProcessingIds((prev) => new Set(prev).add(guaranteeId));

    const userId = localStorage.getItem("saveduserid");
    try {
      if (needsConsent(request)) {
        await handleSubmitConsent(guaranteeId);
      }

      await linkGurantor(userId, guaranteeId);
      setConsentSubmitted(true);
      loadUserTenant("tenant");
      setAcceptedGuaranteeName(
        `${request?.tenant_id?.fname ?? ""} ${request?.tenant_id?.lname ?? ""}`
      );
      loadPendingGuarantees();
      // Doing flatemate null because we dont want to show flatemates if request is accepted by guarantor
      setPendingFlatmatesRequests?.([]);
      // setPendingGuaranteesRequests?.([]);
      handleAcceptModal();
    } catch (e) {
      console.log("Error", e);
      toast.error(
        e?.response?.data?.msg ??
          _t(
            "failedToAcceptRequest",
            "Text used in guarantees pending requests popup"
          )
      );
    } finally {
      // localStorage.removeItem("guarantorFor");
      setProcessingIds((prev) => {
        const updated = new Set(prev);
        updated.delete(guaranteeId);
        return updated;
      });
    }
  };

  const handleReject = async (tenant_id) => {
    if (processingIds.has(tenant_id)) return;
    setProcessingIds((prev) => new Set(prev).add(tenant_id));

    try {
      await rejectGuarantee(tenant_id);
      setPendingFlatmatesRequests?.([]);
      loadPendingGuarantees();
      // setPendingGuaranteesRequests?.([]);
    } catch {
      toast.error(_t("failedToRejectRequest"));
    } finally {
      // localStorage.removeItem("guarantorFor");
      setProcessingIds((prev) => {
        const updated = new Set(prev);
        updated.delete(tenant_id);
        return updated;
      });
    }
  };
  const userName = pendingGuarantees?.[0]?.tenant_id
    ? `${pendingGuarantees[0].tenant_id.fname} ${pendingGuarantees[0].tenant_id.lname}`
    : "";

  return (
    <Modal
      show={show}
      centered
      className={`wrapper-modal newmodalui asguarantor modal-lg pending-request-modal ${
        showBackground ? "modal-fixed-screen" : ""
      }`}
    >
      {tab === "info" && (
        <>
          <Modal.Header>
            <Modal.Title className="pending-modal-title pending-modal-title-info">
              {_t(
                "pendingGuaranteesRequests",
                "Text used in guarantees pending requests popup"
              )}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {loading ? (
              <div className="text-center my-4">
                <img
                  src={SkorDefaultLoader}
                  className="defaultSkorLoader"
                  alt="Example"
                />
              </div>
            ) : pendingGuarantees.length === 0 ? (
              <p className="text-center">
                {_t(
                  "noPendingGuaranteesFound",
                  "Text used in guarantees pending requests popup"
                )}
              </p>
            ) : (
              <div className="d-flex flex-column gap-2 wrapper-pending-requests-modal-info">
                <p>
                  {userName} {_t("guaranteesRequestsDescription")}
                </p>
                {pendingGuarantees.map((request) => {
                  const tenantId = request?.tenant_id?._id;
                  const isProcessing = processingIds.has(tenantId);
                  const showConsent = needsConsent(request);
                  const consentsForThis = getConsentsForRequest(tenantId);
                  const isAcceptDisabled =
                    // This card is already in flight
                    isProcessing ||
                    // Another card is in flight — only one at a time allowed
                    (!isProcessing && isAnyProcessing) ||
                    // Consent required but not fully ticked
                    (showConsent && !allConsentsTicked(tenantId));

                  return (
                    <div key={request._id} className="d-flex flex-column">
                      <div className="d-flex flex-column request-info request-info-card">
                        <p>{_t("tenantRequestingYourGuarantees")}</p>
                        <div className="info-card">
                          <div className="label-name-wrapper">
                            {`${request?.tenant_id?.fname?.charAt(0)?.toUpperCase() ?? ""}${request?.tenant_id?.lname?.charAt(0)?.toUpperCase() ?? ""}`}
                          </div>
                          <div className="d-flex flex-column">
                            <p>
                              {request?.tenant_id?.fname ?? "N/A"}{" "}
                              {request?.tenant_id?.lname ?? ""}
                            </p>
                            <span className="text-muted">
                              {request?.tenant_id?.email}
                            </span>
                          </div>
                        </div>
                      </div>

                      {showConsent && (
                        <>
                          {consentSections.map((section) => (
                            <div
                              key={section.id}
                              className="request-insurance-evaluation-card"
                            >
                              <h3>{section.title}</h3>

                              <ul>
                                {section.points.map((point, index) => (
                                  <li key={index}>{point}</li>
                                ))}
                              </ul>

                              <div
                                style={{
                                  backgroundColor: consentsForThis[section.id]
                                    ? "#EDF8F0"
                                    : "#F6F6F6",
                                }}
                                className="consent-checkbox cursor-pointer"
                                onClick={() =>
                                  handleToggle(tenantId, section.id)
                                }
                              >
                                <label className="custom-checkbox">
                                  <input
                                    type="checkbox"
                                    checked={
                                      consentsForThis[section.id] || false
                                    }
                                    readOnly
                                    onChange={() =>
                                      handleToggle(tenantId, section.id)
                                    }
                                  />
                                  <span className="custom-checkmark"></span>
                                </label>

                                <div>
                                  <p>{section.checkboxLabel}</p>

                                  <div className="d-flex flex-column">
                                    {section.links?.map((linkObj, index) => (
                                      <a
                                        key={index}
                                        href={linkObj.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-decoration-underline cursor-pointer card-link"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        {linkObj.title}
                                        <img
                                          src="/images/redirect-link-img.svg"
                                          alt=""
                                          className="link-icon"
                                        />
                                      </a>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </>
                      )}

                      <div className="d-flex justify-content-center align-items-center gap-2">
                        <button
                          className="btnNext btncancle pending-request-cancel-btn"
                          onClick={() => {
                            setRejectingId({
                              id: request?.tenant_id?._id,
                              guarantorName:
                                (request?.tenant_id?.fname ?? "") +
                                " " +
                                (request?.tenant_id?.lname ?? ""),
                            });
                            setTab("reject");
                          }}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M13.8134 1.08759C13.8726 1.02843 13.9195 0.958196 13.9515 0.8809C13.9835 0.803604 14 0.720759 14 0.637094C14 0.55343 13.9835 0.470585 13.9515 0.393289C13.9195 0.315993 13.8726 0.24576 13.8134 0.1866C13.7542 0.127441 13.684 0.080513 13.6067 0.048496C13.5294 0.016479 13.4466 0 13.3629 0C13.2792 0 13.1964 0.016479 13.1191 0.048496C13.0418 0.080513 12.9716 0.127441 12.9124 0.1866L7 6.10029L1.08759 0.1866C1.02843 0.127441 0.958196 0.080513 0.8809 0.048496C0.803604 0.016479 0.720759 6.23348e-10 0.637094 0C0.55343 -6.23349e-10 0.470585 0.016479 0.393289 0.048496C0.315993 0.080513 0.24576 0.127441 0.1866 0.1866C0.127441 0.24576 0.080513 0.315993 0.048496 0.393289C0.016479 0.470585 -6.23348e-10 0.55343 0 0.637094C6.23349e-10 0.720759 0.016479 0.803604 0.048496 0.8809C0.080513 0.958196 0.127441 1.02843 0.1866 1.08759L6.10029 7L0.1866 12.9124C0.0671221 13.0319 0 13.1939 0 13.3629C0 13.5319 0.0671221 13.6939 0.1866 13.8134C0.306079 13.9329 0.468126 14 0.637094 14C0.806062 14 0.96811 13.9329 1.08759 13.8134L7 7.89971L12.9124 13.8134C13.0319 13.9329 13.1939 14 13.3629 14C13.5319 14 13.6939 13.9329 13.8134 13.8134C13.9329 13.6939 14 13.5319 14 13.3629C14 13.1939 13.9329 13.0319 13.8134 12.9124L7.89971 7L13.8134 1.08759Z"
                              fill="black"
                            />
                          </svg>
                          {_t("reject")}
                        </button>
                        <button
                          className="btnNext btnorange pending-request-accept-btn"
                          onClick={() => handleAccept(request)}
                          disabled={isAcceptDisabled}
                        >
                          <svg
                            width="16"
                            height="14"
                            viewBox="0 0 16 14"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M16 1.26174L5.02857 12L0 7.0783L1.28914 5.81656L5.02857 9.46756L14.7109 0L16 1.26174Z"
                              fill="#253837"
                            />
                          </svg>
                          {_t("acceptAndLink")}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Modal.Body>
        </>
      )}
      {tab === "reject" && (
        <Modal.Body>
          <div className="accept-modal-info reject">
            <div className="accept-icon">!</div>
            <h3>{_t("areYouSureYouWantToReject")}</h3>
            <p>
              {_t("ifYouDontKnow")} {rejectingId?.guarantorName}{" "}
              {_t("orHaveNotGivenYourConsentRejectThisRequest")}
            </p>
            <div className="d-flex reject_btn justify-content-center align-items-center gap-2 w-100">
              <button
                className="btnNext btncancle pending-request-cancel-btn"
                onClick={() => setTab("info")}
              >
                {_t("goBack")}
              </button>
              <button
                className="btnNext btnorange pending-request-accept-btn"
                onClick={() => handleReject(rejectingId?.id)}
              >
                {_t("yesRejectAndDelete")}
              </button>
            </div>
          </div>
        </Modal.Body>
      )}
    </Modal>
  );
};

export default PendingRequestsPopup;
